<template>
  <div class="file-upload-multiple p-3 border rounded bg-white">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <label :for="id" class="form-label fw-bold mb-0 text-dark">{{ label }}</label>
        <i v-if="helpId" class="bi bi-info-circle-fill ms-2 text-primary cursor-pointer"
          @click="$emit('show-help', helpId)" title="Más información"></i>
      </div>
      <small class="text-muted" v-if="files.length">
        {{ files.length }} archivo(s)
      </small>
    </div>

    <div class="input-group input-group-sm mb-3">
      <input type="file" class="form-control" :id="id" :accept="accept" multiple @change="handleFileUpload"
        :disabled="isUploading" />
    </div>

    <div v-if="isUploading" class="progress mb-3" style="height: 5px;">
      <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style="width: 100%"></div>
    </div>

    <div class="file-list-container" v-if="files.length > 0">
      <ul class="list-group list-group-flush">
        <li v-for="(file, index) in files" :key="file.id || index"
          class="list-group-item d-flex justify-content-between align-items-center px-0 py-2">
          
          <div class="d-flex align-items-center text-truncate me-3">
            <i class="bi bi-file-earmark-pdf-fill text-danger me-2 fs-5"></i>
            <div class="d-flex flex-column text-truncate">
              <a v-if="file.url || file.file" :href="buildFileUrl(file.url || file.file)" target="_blank"
                class="text-decoration-none text-dark fw-medium text-truncate" title="Ver archivo">
                {{ file.name }}
              </a>
              <span v-else class="text-dark fw-medium text-truncate">{{ file.name }}</span>
              <small v-if="!file.id" class="text-success" style="font-size: 0.75rem;">
                <i class="bi bi-cloud-arrow-up"></i> Listo para subir
              </small>
            </div>
          </div>

          <button type="button" class="btn btn-outline-danger btn-sm rounded-circle" 
            @click="removeFile(file, index)"
            title="Eliminar archivo">
            <i class="bi bi-trash"></i>
          </button>
        </li>
      </ul>
    </div>

    <div v-else class="text-center py-4 text-muted border rounded bg-light dashed-border">
      <i class="bi bi-files fs-3 d-block mb-2 opacity-50"></i>
      <span class="small">{{ noFileMessage }}</span>
    </div>

    <HelpModal v-if="helpField" :field="helpField" @close="helpField = null" />
  </div>
</template>

<script>
import { ref, watch } from "vue";
import Swal from "sweetalert2";
import fileManagerService from "@/services/api/fileManagerService";
import HelpModal from "@/components/shared/HelpModal.vue";
import axios from "@/plugins/axios";

export default {
  components: { HelpModal },
  props: {
    label: { type: String, required: true },
    id: { type: String, required: true },
    required: { type: Boolean, default: false },
    noFileMessage: { type: String, default: "No se ha seleccionado ningún archivo" },
    accept: { type: String, default: "" },
    helpId: { type: String, default: null },
    initialFiles: { type: Array, default: () => [] },
    viewId: { type: [String, Number], default: null },
    autoUpload: { type: Boolean, default: true }, // Mantenemos la lógica original de subida automática
  },
  emits: ["file-change", "file-removed", "show-help"],
  setup(props, { emit }) {
    const files = ref([...props.initialFiles]);
    const isUploading = ref(false);
    const helpField = ref(null);

    watch(
      () => props.initialFiles,
      (newFiles) => {
        files.value = [...newFiles];
      },
      { immediate: true, deep: true }
    );

    const handleFileUpload = async (event) => {
      const selectedFiles = Array.from(event.target.files);
      if (selectedFiles.length === 0) return;

      // Validación simple de tipo
      const validFiles = props.accept 
        ? selectedFiles.filter(file => file.type === "application/pdf") // Ajusta según tu necesidad exacta
        : selectedFiles;

      if (validFiles.length !== selectedFiles.length) {
        Swal.fire({ icon: "warning", title: "Atención", text: "Solo se permiten archivos PDF." });
      }

      if (props.autoUpload && props.viewId) {
        // Lógica original: Subir inmediatamente
        isUploading.value = true;
        await uploadFiles(validFiles);
        isUploading.value = false;
      } else {
        // Lógica alternativa: Solo agregar al array local (si el padre maneja la subida final)
        validFiles.forEach(file => {
          files.value.push({
            file: file, // Objeto File nativo
            name: file.name,
            extension: 'pdf'
          });
        });
      }

      emit("file-change", files.value);
      event.target.value = ""; // Reset input
    };

    const uploadFiles = async (fileList) => {
      for (const file of fileList) {
        try {
          // Asumimos que fileManagerService devuelve la estructura correcta
          const response = await fileManagerService.uploadFile(file, props.viewId);
          const responseData = response.data.data || response.data;
          
          files.value.push({
            id: responseData.id,
            file: responseData.file, // Path del servidor
            name: responseData.name,
            extension: responseData.extension,
            url: responseData.url || null
          });
        } catch (error) {
          console.error(error);
          Swal.fire({ icon: "error", title: "Error", text: `Falló la subida de ${file.name}` });
        }
      }
    };

    const removeFile = async (file, index) => {
      const result = await Swal.fire({
        title: "¿Eliminar archivo?",
        text: `Se eliminará "${file.name}".`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        // Si tiene ID, está en el servidor y hay que borrarlo vía API
        if (file.id) {
          try {
            await fileManagerService.deleteFile(file.file, props.viewId, file.name, 'pdf', file.id);
            Swal.fire("Eliminado", "El archivo ha sido eliminado.", "success");
          } catch (error) {
            console.error(error);
            Swal.fire("Error", "No se pudo eliminar el archivo del servidor.", "error");
            return; // No borrar del array local si falló en servidor
          }
        }
        
        // Borrar del array local
        files.value.splice(index, 1);
        emit("file-removed", file);
        emit("file-change", files.value);
      }
    };

    const buildFileUrl = (filePath) => {
      if (!filePath || typeof filePath !== 'string') return '#';
      if (filePath.startsWith('http')) return filePath;
      const baseURL = axios.defaults.baseURL || 'http://127.0.0.1:8000/api/v1';
      return `${baseURL.replace('/api/v1', '')}${filePath}`;
    };

    return {
      files,
      isUploading,
      handleFileUpload,
      removeFile,
      buildFileUrl,
      helpField
    };
  },
};
</script>

<style scoped>
.file-upload-multiple {
  transition: box-shadow 0.3s ease;
}

.file-list-container {
  max-height: 250px;
  overflow-y: auto;
  border-top: 1px solid #dee2e6;
}

/* Scrollbar fino para la lista */
.file-list-container::-webkit-scrollbar {
  width: 6px;
}
.file-list-container::-webkit-scrollbar-thumb {
  background-color: #adb5bd;
  border-radius: 10px;
}

.dashed-border {
  border: 2px dashed #dee2e6 !important;
}

.cursor-pointer {
  cursor: pointer;
}
</style>